import { themeColors } from "../../../themeColors";
/**
 * @file   src\components\AgentDashboard\WantAdListing.js
 * @brief  This file is responsible for listing want ads in Agent dashboard
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useRef, useState } from "react";
import { isMarketRestrictedWantAd } from "@/helpers/marketRestriction";
import useIsMobile from "../../../hooks/useIsMobile";
import MobileActionModal from "./MobileActionModal";
import TableMoreIconVertical from "../../../images/table-more-vertical-icon.svg";
import {
  formateDate,
  loggedInUser,
  setLocalStorage,
  truncate,
  formatAddressForDisplay,
  thousandSeperatedValue,
} from "../../../helpers/common";
import { WANT_AD_KEY } from "../../../constants/common";
import { hasAssignedClient } from "../../../helpers/wantAdClient";
import ActionDropdown from "../ActionDropdown";
import AssignClientModal from "../AssignClientModal";
import WantAdStatusModal from "../../WantAdStatus/WantAdStatusModal";
import TableHeader from "../TableHeader";
import { pageURLs } from "@/constants";
import { useNavigate } from "react-router-dom";
import { getWantAdById } from "../../../services/wantAdService";
import CustomLinkModal from "../../Common/CustomLinkModal";
import { checkIcon, draftIcon } from "@/images";
import { isWaitingRequest } from "@/components/NewRequestLine";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";
import { GenericTable } from "@/components";
import { toast } from "react-toastify";
import WantListingShare from "@/components/WantListingShare";
import { createWantAdMatchClickHandler } from "../../../helpers/matchTabRedirect";
import {
  clientFormStatus,
  deriveWantListingStatus,
  wantListingStatusLabel,
} from "../../../helpers/wantListingStatus";
import CloseOutSheet from "@/components/CloseOutSheet";
import WantAdExpiryNote, { isExpiringSoon } from "@/components/WantAdExpiryNote";
import { confirmStillLooking } from "@/services/closeOutLoopService";
import moment from "moment";

const deriveWantAdStatus = (ad) => {
  if (_.get(ad, "IsArchived", "") === 1) return "ARCHIVED";
  if (_.get(ad, "IsClosed", "") === 1 || _.get(ad, "IsTerminated", "") === 1)
    return "CLOSED";
  if (
    _.get(ad, "IsPending", "") === 1 ||
    _.get(ad, "IsPendingWithContigencies", "") === 1
  )
    return "PENDING";
  return "ACTIVE";
};

// Pending, Closed and Archived listings are also stored with IsPublished = 0,
// so only an otherwise-ACTIVE listing that was never published is a draft.
const deriveRowStatus = (ad) => {
  const status = deriveWantAdStatus(ad);
  return status === "ACTIVE" && Number(_.get(ad, "IsPublished", 0)) !== 1
    ? "DRAFT"
    : status;
};

const isShareable = (ad) =>
  Number(_.get(ad, "IsPublished", 0)) === 1 && deriveWantAdStatus(ad) === "ACTIVE";

const AgentWantAdListing = (props) => {

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [actionModal, setActionModal] = useState({ show: false, ad: null });

  const {
    showNoResult,
    handleSort,
    itemList,
    onItemEdit,
    onItemDelete,
    onReviewRequest,
    sort,
    emptyItemHtml,
    hasError,
    loading,
    hasMore,
    loadingMore,
    onShowMore,
  } = props;

  const agent = loggedInUser();

  const [showCustomLinkPopuop, setShowCustomLinkPopup] = useState(false);
  const handleCustomLinkPopupOpen = () => {
    setShowCustomLinkPopup(true);
  };
  const handleCustomLinkPopupClose = () => {
    setShowCustomLinkPopup(false);
  };

  const [linkToShare, setLinkToShare] = useState("");
  const [shareTarget, setShareTarget] = useState(null);
  const shareRequestRef = useRef(null);
  const openShare = async (wantAdId, slug) => {
    if (shareRequestRef.current) return;
    shareRequestRef.current = wantAdId;
    try {
      const response = await getWantAdById(wantAdId);
      if (response?.errorCode === 0 && response?.result) setShareTarget({ ad: response.result, slug });
      else toast.error("Could not open sharing. Please try again.");
    } catch (error) {
      toast.error("Could not open sharing. Please try again.");
    } finally {
      shareRequestRef.current = null;
    }
  };

  const [openDropdownId, setOpenDropdownId] = useState(null);

  const [statusModal, setStatusModal] = useState({
    open: false,
    wantAdId: null,
    status: null,
    title: null,
  });

  const [assignClientModal, setAssignClientModal] = useState({
    show: false,
    wantAdId: null,
  });
  const handleOpenAssignClient = (wantAdId) => {
    setAssignClientModal({ show: true, wantAdId });
  };
  const handleAssignClientClose = () => {
    setAssignClientModal({ show: false, wantAdId: null });
  };
  const handleClientAssigned = () => {
    if (typeof props.onStatusSaved === "function") props.onStatusSaved();
    else if (typeof props.refresh === "function") props.refresh();
  };
  const handleChangeStatus = (wantAdId, status) => {
    if (status === "CLOSED") {
      openCloseOut(wantAdId);
      return;
    }
    const ad = itemList?.find((a) => _.get(a, "Want_ad_Id") === wantAdId);
    setStatusModal({
      open: true,
      wantAdId,
      status,
      title: _.get(ad, "Title_Text", ""),
    });
  };
  const [closeOut, setCloseOut] = useState({ show: false, wantAd: null });
  const openCloseOut = (wantAdId) => {
    const ad = itemList?.find((a) => _.get(a, "Want_ad_Id") === wantAdId);
    setCloseOut({
      show: true,
      wantAd: { wantAdId, title: _.get(ad, "Title_Text", "") },
    });
  };
  const requestDelete = (wantAdId) => {
    const ad = itemList?.find((a) => _.get(a, "Want_ad_Id") === wantAdId);
    const status = ad ? deriveWantAdStatus(ad) : null;
    const isDraft = status === "ACTIVE" && _.get(ad, "IsPublished", 0) != 1;
    // The API treats closed/terminated as final even when also archived.
    const isFinal =
      _.get(ad, "IsClosed", 0) == 1 || _.get(ad, "IsTerminated", 0) == 1;
    if (["ACTIVE", "PENDING", "ARCHIVED"].includes(status) && !isDraft && !isFinal) {
      openCloseOut(wantAdId);
    } else {
      onItemDelete(wantAdId);
    }
  };
  const handleCloseOutHide = () => setCloseOut({ show: false, wantAd: null });
  const handleRemoveWithoutOutcome = (wantAdId) => {
    handleCloseOutHide();
    onItemDelete(wantAdId);
  };
  const handleStatusModalClose = () => {
    setStatusModal({ open: false, wantAdId: null, status: null, title: null });
  };
  const handleStatusSaved = () => {
    if (typeof props.onStatusSaved === "function") props.onStatusSaved();
    else if (typeof props.refresh === "function") props.refresh();
  };
  const handleKeepActive = async (wantAdId) => {
    try {
      const result = await confirmStillLooking(wantAdId);
      toast.success(
        result?.expiryDate
          ? `Kept active until ${moment(result.expiryDate).format("MMM D")}`
          : "Kept active",
      );
      handleStatusSaved();
    } catch (error) {
      toast.error(error?.message || "Could not keep it active. Please try again.");
    }
  };
  const keepActiveFor = (ad) =>
    isShareable(ad) && isExpiringSoon(_.get(ad, "ExpiryDate", null))
      ? handleKeepActive
      : undefined;

  const { shouldRestrictAgent } = useAgentGitchaRestrictions();

  const columns = [
    {
      label: "Date added",
      sorting: "Yes",
      sortKey: "date",
      cell: (ad) => {
        const created = formateDate(_.get(ad, "CreatedOn", ""), "shortMonth");
        return (
          <div className="d-flex align-items-center justify-content-start">
            <span>{created}</span>
          </div>
        );
      },
    },
    {
      label: "Title",
      sorting: "Yes",
      sortKey: "title",
      cell: (ad) => {
        const title = _.get(ad, "Title_Text", "");
        return (
          <a
            href={void 0}
            role="button"
            className="txt-no-underline client-name-style"
          >
            {title ? (
              <span className="d-inline-block client-name-style txt-overflow-style">
                {truncate(title, 26)}
              </span>
            ) : (
              <span
                style={{ fontStyle: "italic" }}
                className="d-inline-block client-name-style txt-overflow-style"
              >
                Untitled
              </span>
            )}
          </a>
        );
      },
    },
    {
      label: "Location",
      sorting: "Yes",
      sortKey: "location",
      cell: (ad) => {
        const rawZip = _.get(ad, "zipCode");
        const zips = Array.isArray(rawZip) ? rawZip : rawZip ? [rawZip] : [];
        const loc = _.get(ad, "Location", "");

        if (zips.length === 0 && !loc) return null;

        const title = zips.length
          ? `Within ZIP(s) code ${zips.join(", ")}`
          : loc;
        const txt = zips.length
          ? `ZIP ${zips[0]}${zips.length > 1 ? ` +${zips.length - 1}` : ""}`
          : loc;

        return (
          <div className="d-flex align-items-center justify-content-start">
            <div className="property-location-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.25 8C11.0074 8 10 9.00736 10 10.25C10 11.4926 11.0074 12.5 12.25 12.5C13.4926 12.5 14.5 11.4926 14.5 10.25C14.5 9.00736 13.4926 8 12.25 8ZM8.5 10.25C8.5 8.17893 10.1789 6.5 12.25 6.5C14.3211 6.5 16 8.17893 16 10.25C16 12.3211 14.3211 14 12.25 14C10.1789 14 8.5 12.3211 8.5 10.25Z"
                  fill="#15181E"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.25 3.5C10.4598 3.5 8.7429 4.21116 7.47703 5.47703C6.21116 6.7429 5.5 8.45979 5.5 10.25C5.5 13.355 7.23389 16.1822 9.06595 18.2891C9.97283 19.332 10.8816 20.1729 11.5639 20.7528C11.8337 20.9821 12.0672 21.1699 12.25 21.3125C12.4328 21.1699 12.6663 20.9821 12.9361 20.7528C13.6184 20.1729 14.5272 19.332 15.434 18.2891C17.2661 16.1822 19 13.355 19 10.25C19 8.45979 18.2888 6.7429 17.023 5.47703C15.7571 4.21116 14.0402 3.5 12.25 3.5ZM12.25 22.25C11.8199 22.8644 11.8197 22.8643 11.8195 22.8641L11.8188 22.8637L11.8171 22.8624L11.8115 22.8585L11.7925 22.845C11.7763 22.8334 11.7531 22.8167 11.7236 22.7952C11.6644 22.752 11.5794 22.689 11.4723 22.6071C11.2581 22.4434 10.9549 22.2038 10.5924 21.8957C9.86838 21.2803 8.90217 20.3867 7.93405 19.2734C6.01611 17.0678 4 13.895 4 10.25C4 8.06196 4.86919 5.96354 6.41637 4.41637C7.96354 2.86919 10.062 2 12.25 2C14.438 2 16.5365 2.86919 18.0836 4.41637C19.6308 5.96354 20.5 8.06196 20.5 10.25C20.5 13.895 18.4839 17.0678 16.566 19.2734C15.5978 20.3867 14.6316 21.2803 13.9076 21.8957C13.5451 22.2038 13.2419 22.4434 13.0277 22.6071C12.9206 22.689 12.8356 22.752 12.7764 22.7952C12.7469 22.8167 12.7237 22.8334 12.7075 22.845L12.6885 22.8585L12.6829 22.8624L12.6812 22.8637L12.6805 22.8641C12.6803 22.8643 12.6801 22.8644 12.25 22.25ZM12.25 22.25L12.6805 22.8641C12.4223 23.0449 12.0777 23.0449 11.8195 22.8641L12.25 22.25Z"
                  fill="#15181E"
                />
              </svg>
            </div>
            <span
              className="d-inline-block client-property-style"
              title={title}
            >
              {formatAddressForDisplay(txt)}
            </span>
          </div>
        );
      },
    },
    {
      label: "Status",
      sorting: "Yes",
      sortKey: "status",
      cell: (ad) => {
        const status = deriveWantListingStatus(ad);
        const label = wantListingStatusLabel(status);

        if (status === "withdrawn" || status === "expired")
          return <span className="table-badge-orange me-2">{label}</span>;

        if (status === "closed")
          return <span className="table-badge-grey me-2">{label}</span>;

        if (status === "under_contract")
          return <span className="table-badge-blue me-2">{label}</span>;

        if (status === "active")
          return (
            <div>
              <span className="table-badge-bg">
                <img src={checkIcon} style={{ marginRight: 8 }} />
                <span className="d-inline-block align-text-center" />
                {label}
              </span>
              <WantAdExpiryNote expiryDate={_.get(ad, "ExpiryDate", null)} />
            </div>
          );

        const formNote = clientFormStatus(ad);
        const canReview = Boolean(onReviewRequest) && isWaitingRequest(ad);
        return (
          <div className="d-flex align-items-center justify-content-start">
            <img
              src={draftIcon}
              style={{ width: 18, height: 18, marginRight: 8 }}
            />
            {canReview ? (
              <button
                type="button"
                className="btn btn-link p-0 d-inline-block client-property-style text-nowrap"
                style={{ color: "var(--primary-500)" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onReviewRequest(ad);
                }}
              >
                {formNote}
              </button>
            ) : (
              <span className="d-inline-block client-property-style text-nowrap">
                {formNote || label}
              </span>
            )}
          </div>
        );
      },
    },
    {
      label: '"Perfect Fit Matches"',
      sorting: "Yes",
      sortKey: "perfectFits",
      cell: (ad) => {
        const isPublished = _.get(ad, "IsPublished");
        const perfectFits = isPublished ? _.get(ad, "perfectFits", "") : 0;

        const hasPerfectFits = perfectFits > 0;
        // AI has not ranked this want-ad yet - the legacy count may be
        // contradicted later, so hold the pill back (missing key = not pending).
        const aiPending = ad?.aiPending === true;

        // Nothing is matched against a want-listing that isn't live, so there
        // is no count to report and no ranking on the way. "Scoring listings…"
        // promised one that never arrives; a bare 0 read as "we looked and
        // found none". Both are wrong for a draft or a paused listing.
        if (!isPublished) {
          return (
            <span className="perfect-fit-na" title="Not live, so no matches yet">
              --
            </span>
          );
        }

        if (aiPending) {
          return (
            <div
              className="want-ad-tag ai-scoring-tag mb-0"
              title="Scoring listings for this buyer"
              onClick={(e) => e.stopPropagation()}
            >
              Scoring listings…
            </div>
          );
        }

        const handleNavigateToMatches = createWantAdMatchClickHandler(navigate, {
          wantAdId: ad?.Want_ad_Id,
          slug: ad?.slug,
          hasMatches: hasPerfectFits,
        });

        return (
          <div
            className={
              hasPerfectFits
                ? "want-ad-tag medium-blue-tag-bg-border medium-blue-tag-bg-border-clickable mb-0"
                : ""
            }
            onClick={handleNavigateToMatches}
          >
            {thousandSeperatedValue(perfectFits)} {hasPerfectFits ? "Active listings" : ""}
          </div>
        );
      },
    },
    {
      label: "Intros received",
      sorting: "Yes",
      sortKey: "intros",
      cell: (ad) => _.get(ad, "IntroReceived", ""),
    },
    {
      label: "Client",
      sorting: "Yes",
      sortKey: "client",
      cell: (ad) => {
        const firstName = _.get(ad, "FirstName", "");
        const lastName = _.get(ad, "LastName", "");
        const hasClient = hasAssignedClient(ad);
        const client = hasClient
          ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
          : String(_.get(ad, "RequesterName", "") || "").trim() || "N/A";
        const wantAdId = _.get(ad, "Want_ad_Id", "");
        const canAssignClient = !hasClient && !shouldRestrictAgent;
        return (
          <div className="agent-want-listing-dropdown">
            <p
              className="agent-want-listing-dropdown-paragraph mb-0"
              role={canAssignClient ? "button" : undefined}
              tabIndex={canAssignClient ? 0 : undefined}
              style={canAssignClient ? { cursor: "pointer" } : undefined}
              onClick={
                canAssignClient
                  ? (e) => {
                      e.stopPropagation();
                      handleOpenAssignClient(wantAdId);
                    }
                  : undefined
              }
              onKeyDown={
                canAssignClient
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpenAssignClient(wantAdId);
                      }
                    }
                  : undefined
              }
            >
              {client ?? ""}
            </p>
            {shouldRestrictAgent ? <></> : (
              <div className="social-btns-wrap no-bg-toggle ms-1" onClick={(e) => e.stopPropagation()}>
                {isMobile ? (
                  <img
                    src={TableMoreIconVertical}
                    alt="actions"
                    role="button"
                    onClick={() => setActionModal({ show: true, ad })}
                  />
                ) : (
                  <ActionDropdown
                    onItemEdit={onItemEdit}
                    disableEdit={
                      _.get(ad, "IsFilledByClient", 0) == 1 &&
                      _.get(ad, "IsReadyToReview", 0) == 0
                    }
                    onItemDelete={requestDelete}
                    editText="Edit Want-Listing"
                    deleteText="Delete Want-Listing"
                    itemId={wantAdId}
                    buyerListLinkText="BuyerListing Form"
                    client_Want_ad_Id={_.get(ad, "Client_Want_ad_Id", "")}
                    showBuyerListingFormUrl={showBuyerListingFormUrl}
                    onChangeStatus={handleChangeStatus}
                    onKeepActive={keepActiveFor(ad)}
                    currentStatus={deriveRowStatus(ad)}
                    show={openDropdownId === wantAdId}
                    onToggle={(isOpen) =>
                      setOpenDropdownId(isOpen ? wantAdId : null)
                    }
                    onAssignClient={handleOpenAssignClient}
                    assignClientText={hasClient ? "Change client" : "Assign client"}
                    onShare={
                      isShareable(ad)
                        ? () => openShare(wantAdId, ad.slug)
                        : undefined
                    }
                    shareText="Share Want-Listing"
                    isMarketRestricted={isMarketRestrictedWantAd(ad)}
                  />
                )}
              </div>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean);

  /**
   * To handle navigation to details page
   * @param {any} wantAdId
   */
  const handleWantAdItemClick = (wantAdId, slug, isPublished, ad) => {

    if (!isPublished && onReviewRequest && isWaitingRequest(ad)) {
      return onReviewRequest(ad);
    }

    if (!isPublished) {
      return onItemEdit(wantAdId);
    }

    setLocalStorage(WANT_AD_KEY, { wantAdId: wantAdId, introId: 1 });
    navigate(`${pageURLs.wantAdDetails}/${slug}`);
  };

  const showBuyerListingFormUrl = (client_Want_ad_Id) => {
    setLinkToShare(
      process.env.REACT_APP_BUYERLISTING_SERVICE_BASE_URL +
      "/form/" +
      agent?.userInfo?.firstName +
      "-" +
      agent?.userInfo?.lastName +
      "-" +
      client_Want_ad_Id,
    );
    handleCustomLinkPopupOpen();
  };

  return (
    <>
      {shareTarget ? (
        <WantListingShare
          adDetails={shareTarget.ad}
          slug={shareTarget.slug}
          onHide={() => setShareTarget(null)}
        />
      ) : null}
      <CustomLinkModal
        show={showCustomLinkPopuop}
        onHide={handleCustomLinkPopupClose}
        linkToShare={linkToShare}
      />
      <WantAdStatusModal
        show={statusModal.open}
        wantAdId={statusModal.wantAdId}
        status={statusModal.status}
        title={statusModal.title}
        onHide={handleStatusModalClose}
        onSaved={handleStatusSaved}
      />
      <CloseOutSheet
        show={closeOut.show}
        wantAd={closeOut.wantAd}
        onHide={handleCloseOutHide}
        onRemoveWithoutOutcome={handleRemoveWithoutOutcome}
        onClosedOut={handleStatusSaved}
      />
      <AssignClientModal
        show={assignClientModal.show}
        wantAdId={assignClientModal.wantAdId}
        onHide={handleAssignClientClose}
        onSaved={handleClientAssigned}
      />
      <div className="dashboard-table-wrap">
      <div className="table-responsive wantad-table-responsive">
          {showNoResult && itemList !== null && itemList?.length > 0 ? (
            <GenericTable
              data={itemList}
              className="table"
              renderHeader={() => (
                <TableHeader
                  columns={columns}
                  handleSort={handleSort}
                  sort={sort}
                />
              )}
              renderRow={(ad) =>
                columns.map((c, idx) => <td key={idx} onClick={() => handleWantAdItemClick(ad.Want_ad_Id, ad.slug, ad.IsPublished, ad)} style={{ cursor: 'pointer' }}>{c.cell(ad)}</td>)
              }
            />
          ) : (
            !loading &&
            (showNoResult || hasError) &&
            (itemList == null || itemList?.length == 0) &&
            emptyItemHtml()
          )}
        </div>

        {showNoResult && hasMore && itemList?.length > 0 && (
          <div className="d-flex justify-content-center pt-24 pb-24">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onShowMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading..." : "Show more"}
            </button>
          </div>
        )}
      </div>

      {actionModal.ad && (() => {
        const ad = actionModal.ad;
        const wantAdId = _.get(ad, "Want_ad_Id", "");
        const clientWantAdId = _.get(ad, "Client_Want_ad_Id", "");
        const disableEdit =
          _.get(ad, "IsFilledByClient", 0) == 1 &&
          _.get(ad, "IsReadyToReview", 0) == 0;

        const hasClient = hasAssignedClient(ad);
        const status = deriveRowStatus(ad);
        // GTCH-853: no route back to ACTIVE where the market posts through
        // the BLS. Archiving stays available.
        const marketRestricted = isMarketRestrictedWantAd(ad);
        const reactivateAction = marketRestricted
          ? []
          : [
              { label: "Re-activate", onClick: () => handleChangeStatus(wantAdId, "ACTIVE") },
            ];
        const keepActive = keepActiveFor(ad);
        const keepActiveAction = keepActive
          ? [{ label: "Keep active 30 more days", onClick: () => keepActive(wantAdId) }]
          : [];
        const statusActions =
          status === "ACTIVE"
            ? [
                ...keepActiveAction,
                { label: "Mark under contract", onClick: () => handleChangeStatus(wantAdId, "PENDING") },
                { label: "Close out (purchased)", onClick: () => handleChangeStatus(wantAdId, "CLOSED") },
                { label: "Archive listing", onClick: () => handleChangeStatus(wantAdId, "ARCHIVED") },
              ]
            : status === "PENDING"
            ? [
                { label: "Close out (purchased)", onClick: () => handleChangeStatus(wantAdId, "CLOSED") },
                ...reactivateAction,
              ]
            : reactivateAction;

        return (
          <MobileActionModal
            show={actionModal.show}
            onHide={() => setActionModal({ show: false, ad: null })}
            actions={[
              ...(isShareable(ad)
                ? [
                    {
                      label: "Share Want-Listing",
                      onClick: () => {
                        setActionModal({ show: false, ad: null });
                        openShare(wantAdId, ad.slug);
                      },
                    },
                  ]
                : []),
              {
                label: disableEdit ? "BuyerListing Form" : "Edit Want-Listing",
                onClick: () => disableEdit ? showBuyerListingFormUrl(clientWantAdId) : onItemEdit(wantAdId),
              },
              {
                label: hasClient ? "Change client" : "Assign client",
                onClick: () => handleOpenAssignClient(wantAdId),
              },
              ...statusActions,
              {
                label: "Delete Want-Listing",
                color: "#dc3545",
                onClick: () => {
                  setActionModal({ show: false, ad: null });
                  requestDelete(wantAdId);
                },
              },
            ]}
          />
        );
      })()}
    </>
  );
};
export default AgentWantAdListing;
